import React from "react";
import { useTranslation } from "react-i18next";

import { ExerciseType } from "@fitness-app/data-models/entities/Exercise";
import { type ExerciseInWorkout } from "@fitness-app/data-models/entities/TrainingProgram";
import { formatFitnessNumber } from "@fitness-app/utils/src/programs/formatFitnessNumber";
import { reduceExerciseProperty } from "@fitness-app/utils/src/programs/reduceExerciseProperty";

interface ExerciseInfoProps {
  exerciseInWorkout: ExerciseInWorkout;
  separator?: React.ReactNode;
  showRestBetweenSeries?: boolean;
}

const renderWorkoutsProperties = (properties: number | [number, number] | number[] | null) => {
  if (!properties) {
    return "-";
  }
  if (typeof properties === "number") {
    return <span>{formatFitnessNumber(properties)}</span>;
  }
  if (properties[1] === properties[0]) {
    return <span>{formatFitnessNumber(properties[0])}</span>;
  }
  return (
    <span>
      {formatFitnessNumber(properties[0])} - {formatFitnessNumber(properties[1])}
    </span>
  );
};

const hasMeaningfulWeight = (weight: number | [number, number] | number[] | null): boolean => {
  if (weight === null) {
    return false;
  }
  return typeof weight === "number" ? weight !== 0 : weight.some((value) => value !== 0);
};

const ExerciseInfo = ({ exerciseInWorkout, separator = "|", showRestBetweenSeries = false }: ExerciseInfoProps) => {
  const { t } = useTranslation("workouts");

  const weight = reduceExerciseProperty(exerciseInWorkout.set, "weight");
  const showWeight = exerciseInWorkout.exercise.type !== ExerciseType.CARDIO && hasMeaningfulWeight(weight);
  const repeats = reduceExerciseProperty(exerciseInWorkout.set, "repeats");
  // Timed exercises (plank, hang) are planned with 0 repeats - "0 powt." only adds noise there.
  const showRepeats = hasMeaningfulWeight(repeats) || !exerciseInWorkout.duration?.value;
  const showRest = showRestBetweenSeries && exerciseInWorkout.numberOfSeries > 1 && Boolean(exerciseInWorkout.restTime);

  return (
    <>
      <span>{t("seriesCount", { count: exerciseInWorkout.numberOfSeries })}</span>
      {showRepeats && (
        <>
          <span>{separator}</span>
          <span>
            {renderWorkoutsProperties(repeats)} {t("repeatsAbv")}
          </span>
        </>
      )}
      {showWeight && (
        <>
          <span>{separator}</span>
          <span>{renderWorkoutsProperties(weight)} kg</span>
        </>
      )}
      {exerciseInWorkout.duration?.value && typeof exerciseInWorkout.duration.value === "number" && (
        <>
          <span>{separator}</span>
          <span>
            {renderWorkoutsProperties(reduceExerciseProperty(exerciseInWorkout.set, "duration"))}{" "}
            {t(`duration.${exerciseInWorkout.duration.format}`)}
          </span>
        </>
      )}
      {showRest && (
        <>
          <span>{separator}</span>
          <span>{t("restBetweenSeriesShort", { seconds: exerciseInWorkout.restTime })}</span>
        </>
      )}
    </>
  );
};

export default ExerciseInfo;
